import React from 'react';
import { Helmet } from 'react-helmet-async';
// @ts-ignore
import { useSelector } from 'react-redux';
// @ts-ignore
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import { useCanonicalUrl } from './CanonicalUrlContext';

export interface SeoProps {
  title?: string;
  titleTemplate?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: 'website' | 'article' | 'product';
  ogSiteName?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  twitterSite?: string;
  twitterCreator?: string;
  noIndex?: boolean;
  /** Partial hreflang signal only: full clusters need locale-prefixed URLs. */
  language?: string;
  children?: React.ReactNode;
  jsonLd?: Record<string, unknown>;
}

function deriveCanonicalUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.location.href.split('#')[0]; // strip hash
  } catch {
    return undefined;
  }
}

function deriveBaseUrl(absoluteUrl: string): string {
  try {
    const url = new URL(absoluteUrl);
    return `${url.protocol}//${url.host}/`;
  } catch {
    return '/';
  }
}

const DEFAULT_DESCRIPTION =
  'Reservá tus vacaciones y paquetes turísticos con los mejores precios y financiación. Vuelos, hoteles, traslados y excursiones.';

export function Seo({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  ogSiteName,
  twitterCard = 'summary_large_image',
  twitterSite,
  twitterCreator,
  noIndex = false,
  language,
  jsonLd,
  children,
}: SeoProps): React.ReactElement {
  // Multi-tenant defaults from Redux (available during SSR)
  const appName = useAppName();
  const templateLogo: string = useSelector(
    (state: any) => state?.applicationState?.templateData?.header?.logo || '',
  );

  // SSR canonical from context (set once per request in server.tsx)
  const contextCanonical = useCanonicalUrl();

  // Resolve values: explicit prop > context (SSR) > window.location (CSR)
  const resolvedPageTitle = title || appName || '';
  const resolvedBrowserTitle = appName || resolvedPageTitle;
  const resolvedDescription = description || DEFAULT_DESCRIPTION;
  const resolvedCanonical = canonicalUrl ?? contextCanonical ?? deriveCanonicalUrl();
  const resolvedOgImage = ogImage || templateLogo;
  const resolvedOgImageAlt = ogImageAlt || resolvedPageTitle;
  const resolvedOgSiteName = ogSiteName || appName || '';
  const resolvedTwitterCard = twitterCard;

  // Build meta tag array
  const metaTags: any[] = [
    { name: 'description', content: resolvedDescription },
    { property: 'og:title', content: resolvedPageTitle },
    { property: 'og:description', content: resolvedDescription },
    { property: 'og:type', content: ogType },
    { name: 'twitter:card', content: resolvedTwitterCard },
    { name: 'twitter:title', content: resolvedPageTitle },
    { name: 'twitter:description', content: resolvedDescription },
  ];

  // og:image
  if (resolvedOgImage) {
    metaTags.push({ property: 'og:image', content: resolvedOgImage });
    metaTags.push({ name: 'twitter:image', content: resolvedOgImage });
    if (resolvedOgImageAlt) {
      metaTags.push({ property: 'og:image:alt', content: resolvedOgImageAlt });
      metaTags.push({ name: 'twitter:image:alt', content: resolvedOgImageAlt });
    }
  }

  // og:url (canonical)
  if (resolvedCanonical) {
    metaTags.push({ property: 'og:url', content: resolvedCanonical });
    metaTags.push({ name: 'twitter:url', content: resolvedCanonical });
  }

  // og:site_name
  if (resolvedOgSiteName) {
    metaTags.push({ property: 'og:site_name', content: resolvedOgSiteName });
  }

  // Twitter handles
  if (twitterSite) {
    metaTags.push({ name: 'twitter:site', content: twitterSite });
  }
  if (twitterCreator) {
    metaTags.push({ name: 'twitter:creator', content: twitterCreator });
  }

  // Robots
  if (noIndex) {
    metaTags.push({ name: 'robots', content: 'noindex, nofollow' });
  }

  // Build link tags (canonical + hreflang)
  const linkTags: any[] = [];
  if (resolvedCanonical) {
    linkTags.push({ rel: 'canonical', href: resolvedCanonical });
  }

  // Hreflang: self-referencing + x-default (partial signal — full cluster
  // requires locale-prefixed URLs which this app does not currently support).
  if (language && resolvedCanonical) {
    const baseUrl = deriveBaseUrl(resolvedCanonical);

    // Self-referencing: tells Google "this page is in language X"
    linkTags.push({
      rel: 'alternate',
      hreflang: language,
      href: resolvedCanonical,
    });

    // x-default: fallback page (homepage = language selector / default locale)
    linkTags.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: baseUrl,
    });

    // Bing signal: content-language (Bing treats hreflang as weak signal)
    metaTags.push({
      'http-equiv': 'content-language',
      content: language,
    });
  }

  return (
    <Helmet
      defaultTitle={appName || undefined}
      link={linkTags.length > 0 ? linkTags : undefined}
      meta={metaTags}
      title={resolvedBrowserTitle || undefined}
    >
      {jsonLd && (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd),
          }}
          type='application/ld+json'
        />
      )}
      {children}
    </Helmet>
  );
}

export default Seo;
